import { useState, useMemo } from "react";
import { SlidersHorizontal } from "lucide-react";
import { useAdminData } from "../../hooks/useAdminData";
import { Card } from "../../components/ui/Card";
import { FilterDrawer, FilterFieldDef } from "../../components/ui/FilterDrawer";
import { PageHeader } from "../../components/ui/PageHeader";
import { SearchBar } from "../../components/ui/SearchBar";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { CORAL, F, T } from "../../theme/tokens";
import { TableWrap } from "../../components/ui/TableWrap";

// ── Applications ──────────────────────────────────────────────────────────────

export function ApplicationsView() {
  const { ADMIN_APPLICATIONS, PLATFORM_STATS } = useAdminData();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [orgFilter, setOrgFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const uniqueOrgs = useMemo(() => ["All", ...Array.from(new Set(ADMIN_APPLICATIONS.map((a) => a.orgName))).sort()], [ADMIN_APPLICATIONS]);
  const uniqueCategories = useMemo(() => ["All", ...Array.from(new Set(ADMIN_APPLICATIONS.map((a) => a.talentCategory))).sort()], [ADMIN_APPLICATIONS]);

  const appFilterFields: FilterFieldDef[] = [
    { type: "chips", key: "status", label: "Status", options: ["All", "Applied", "Viewed", "Shortlisted", "Hired", "Rejected", "Withdrawn"] },
    { type: "select", key: "org", label: "Organisation", options: uniqueOrgs },
    { type: "select", key: "category", label: "Category", options: uniqueCategories },
  ];
  const filterValues = { status: statusFilter, org: orgFilter, category: categoryFilter };

  const filtered = useMemo(() => ADMIN_APPLICATIONS.filter((a) => {
    const s = search.toLowerCase();
    const match = !s || a.talentName.toLowerCase().includes(s) || a.orgName.toLowerCase().includes(s) || a.postingTitle.toLowerCase().includes(s);
    const ms = statusFilter === "All" || a.status === statusFilter;
    const mo = orgFilter === "All" || a.orgName === orgFilter;
    const mc = categoryFilter === "All" || a.talentCategory === categoryFilter;
    return match && ms && mo && mc;
  }), [ADMIN_APPLICATIONS, search, statusFilter, orgFilter, categoryFilter]);

  const hasFilter = statusFilter !== "All" || orgFilter !== "All" || categoryFilter !== "All";

  return (
    <div>
      <PageHeader title="Applications" sub={`${PLATFORM_STATS.totalApplications.toLocaleString()} total applications across platform`} />

      <FilterDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Applications" fields={appFilterFields} values={filterValues}
        onApply={(v) => { setStatusFilter(v.status ?? "All"); setOrgFilter(v.org ?? "All"); setCategoryFilter(v.category ?? "All"); }} />

      <div style={{ display: "flex", gap: "10px", marginBottom: "18px", flexWrap: "wrap", alignItems: "center" }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search applications…" />
        <button className="filter-btn" onClick={() => setDrawerOpen(true)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 13px", borderRadius: "8px", fontSize: "12px", fontFamily: F, fontWeight: 500, cursor: "pointer", border: `1px solid ${hasFilter ? CORAL : T.border}`, background: hasFilter ? `rgba(209,0,75,0.06)` : T.s2, color: hasFilter ? CORAL : T.sub, transition: "all 0.15s" }}>
          <SlidersHorizontal size={13} /> Filters
        </button>
        {hasFilter && (
          <button onClick={() => { setStatusFilter("All"); setOrgFilter("All"); setCategoryFilter("All"); }} style={{ fontSize: "11px", color: CORAL, fontFamily: F, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Clear</button>
        )}
      </div>
      <Card>
        <TableWrap><table style={{ width: "100%", borderCollapse: "collapse", fontFamily: F }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {["Talent", "Posting", "Organisation", "Rate", "Status", "Applied", "Updated"].map((h) => (
                <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id} style={{ borderBottom: `1px solid ${T.border}`, transition: "background 0.1s" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.s2; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; }}>
                <td style={{ padding: "12px 14px" }}>
                  <p style={{ fontSize: "12px", fontWeight: 600, color: T.text, fontFamily: F }}>{a.talentName}</p>
                  <p style={{ fontSize: "10px", color: T.sub, fontFamily: F }}>{a.talentCategory}</p>
                </td>
                <td style={{ padding: "12px 14px", fontSize: "12px", color: T.text, maxWidth: "200px" }}>
                  <p style={{ fontFamily: F, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.postingTitle}</p>
                </td>
                <td style={{ padding: "12px 14px", fontSize: "12px", color: T.sub, fontFamily: F }}>{a.orgName}</td>
                <td style={{ padding: "12px 14px", fontSize: "12px", color: T.text, fontWeight: 500, fontFamily: F }}>{a.proposedRate}</td>
                <td style={{ padding: "12px 14px" }}><StatusBadge status={a.status} /></td>
                <td style={{ padding: "12px 14px", fontSize: "11px", color: T.muted, fontFamily: F }}>{a.appliedDate}</td>
                <td style={{ padding: "12px 14px", fontSize: "11px", color: T.muted, fontFamily: F }}>{a.updatedDate}</td>
              </tr>
            ))}
          </tbody>
        </table></TableWrap>
      </Card>
    </div>
  );
}
